<template>
  <div class="page-container reveal">
    <el-card class="main-list-card">
      <div class="list-header">
        <div class="header-content">
          <h2 class="page-title">Journal d'Audit</h2>
          <p class="page-subtitle">Historique des actions administratives</p>
        </div>
        <div class="header-actions flex-wrap">
          <el-select v-model="actionFilter" clearable placeholder="Filtrer par action" class="w-56" @change="loadPage(1)">
            <el-option v-for="(label, val) in ACTION_LABELS" :key="val" :label="label" :value="val" />
          </el-select>
          <el-select
            v-model="userFilter"
            filterable
            remote
            clearable
            reserve-keyword
            placeholder="Admin / utilisateur"
            class="w-64"
            :remote-method="searchUsers"
            :loading="usersLoading"
            @change="loadPage(1)"
          >
            <el-option
              v-for="user in userOptions"
              :key="user.id"
              :label="`${user.full_name || 'Sans nom'} · ${user.phone}`"
              :value="user.id"
            />
          </el-select>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            range-separator="→"
            start-placeholder="Début"
            end-placeholder="Fin"
            value-format="YYYY-MM-DD"
            class="!w-60"
            @change="loadPage(1)"
          />
          <el-button @click="loadPage(currentPage)" :loading="isLoading" circle :icon="Refresh" />
        </div>
      </div>

      <div v-if="isLoading" class="flex justify-center p-12">
        <el-icon class="is-loading text-4xl text-profissa-accent"><Loading /></el-icon>
      </div>

      <el-empty v-else-if="!logs.length" description="Aucune action enregistrée" />

      <div v-else class="log-list">
        <div v-for="log in logs" :key="log.id" class="log-entry">
          <div class="log-dot" :class="dotClass(log.action)"></div>
          <div class="log-body">
            <div class="log-header-row">
              <span class="log-action">{{ actionLabel(log.action) }}</span>
              <span class="log-time">{{ fmtDate(log.created_at) }}</span>
            </div>
            <div class="log-meta">
              <span class="log-admin">
                <el-icon><UserFilled /></el-icon>
                {{ log.admin_name || log.admin_id?.slice(0, 8) || 'Système' }}
              </span>
              <span v-if="log.entity_type" class="log-entity">
                {{ entityLabel(log.entity_type) }} · <span class="font-mono">#{{ String(log.entity_id || '').slice(0, 8).toUpperCase() }}</span>
              </span>
              <span v-if="log.ip_address" class="log-entity text-profissa-text-dim">
                IP {{ log.ip_address }}
              </span>
            </div>
            <div v-if="log.payload && Object.keys(log.payload).length" class="log-details">
              {{ JSON.stringify(log.payload) }}
            </div>
          </div>
        </div>
      </div>

      <div class="flex justify-end mt-4 pt-4 border-t border-profissa-glass-border">
        <el-pagination
          v-model:current-page="currentPage"
          :page-size="pageSize"
          :total="totalCount"
          layout="prev, pager, next, total"
          background
          @current-change="loadPage"
        />
      </div>
    </el-card>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Refresh, Loading, UserFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import { listAuditLogs, listAllUsers } from '../services/adminApi'
import { extractApiError } from '../api'

const logs        = ref([])
const isLoading   = ref(false)
const actionFilter = ref('')
const userFilter  = ref('')
const userOptions = ref([])
const usersLoading = ref(false)
const dateRange   = ref(null)
const currentPage = ref(1)
const pageSize    = 50
const totalCount  = ref(0)
const route       = useRoute()

const ACTION_LABELS = {
  APPROVE_ARTISAN:           'Artisan approuvé',
  REJECT_ARTISAN:            'Artisan rejeté',
  SANCTION_ARTISAN:          'Sanction artisan',
  REQUEST_MODIFICATION:      'Correction demandée',
  RESOLVE_DISPUTE:           'Litige résolu',
  RESOLVE_SAV:               'SAV résolu',
  FORCE_CLOSE_INTERVENTION:  'Clôture forcée',
  UPDATE_FRAUD_POLICY:       'Politique fraude modifiée',
  CREATE_CATEGORY:           'Catégorie créée',
  DELETE_CATEGORY:           'Catégorie supprimée',
  TOGGLE_CLIENT:             'Client bloqué/débloqué',
  CREATE_WITHDRAWAL_REQUEST: 'Demande de retrait créée',
  PROCESS_WITHDRAWAL_REQUEST:'Retrait traité',
  ADMIN_TRANSFER:            'Virement admin',
  CREATE_USER:               'Utilisateur créé',
  UPDATE_USER:               'Utilisateur modifié',
  DELETE_USER:               'Utilisateur supprimé',
}
const actionLabel = (a) => ACTION_LABELS[a] || a || '—'

const ENTITY_LABELS = {
  artisan: 'Artisan', intervention: 'Intervention', dispute: 'Litige',
  sav_ticket: 'SAV', category: 'Catégorie', user: 'Utilisateur',
  WithdrawalRequest: 'Retrait', Finance: 'Finance',
}
const entityLabel = (e) => ENTITY_LABELS[e] || e || ''

const fmtDate = (d) => d
  ? new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : '—'

const dotClass = (action) => {
  const a = (action || '').toLowerCase()
  if (a.includes('reject') || a.includes('sanction') || a.includes('delete') || a.includes('suspend')) return 'dot-danger'
  if (a.includes('approve') || a.includes('resolve') || a.includes('create')) return 'dot-success'
  if (a.includes('force') || a.includes('toggle') || a.includes('transfer')) return 'dot-warning'
  return 'dot-info'
}

const searchUsers = async (query = '') => {
  usersLoading.value = true
  try {
    userOptions.value = await listAllUsers(query.trim(), '')
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    usersLoading.value = false
  }
}

const loadPage = async (page = 1) => {
  currentPage.value = page
  isLoading.value = true
  try {
    const offset = (page - 1) * pageSize
    const result = await listAuditLogs({
      limit: pageSize,
      offset,
      action: actionFilter.value || undefined,
      userId: userFilter.value || undefined,
      fromDate: dateRange.value?.[0] || undefined,
      toDate: dateRange.value?.[1] || undefined,
    })
    logs.value = result.items
    totalCount.value = result.total
  } catch (e) {
    ElMessage.error(extractApiError(e))
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  if (route.query.userId) {
    userFilter.value = String(route.query.userId)
  }
  if (route.query.userLabel && route.query.userId) {
    userOptions.value = [{
      id: String(route.query.userId),
      full_name: String(route.query.userLabel),
      phone: '',
    }]
  }
  await searchUsers('')
  await loadPage(1)
})
</script>

<style scoped>
.page-container { @apply h-full flex flex-col overflow-y-auto pb-8; }
.main-list-card { @apply flex-1; }
.list-header { @apply flex justify-between items-end pb-6 gap-4; }
.page-title { @apply text-2xl font-black mb-1; }
.page-subtitle { @apply text-profissa-text-muted text-sm font-semibold; }
.header-actions { @apply flex gap-3 items-center; }

.log-list { @apply flex flex-col gap-0; }
.log-entry { @apply flex gap-4 py-4 border-b border-profissa-glass-border last:border-0; }
.log-dot { @apply w-3 h-3 rounded-full mt-1.5 flex-shrink-0; }
.dot-danger { @apply bg-red-500; }
.dot-success { @apply bg-green-500; }
.dot-warning { @apply bg-orange-500; }
.dot-info { @apply bg-blue-400; }

.log-body { @apply flex-1 min-w-0; }
.log-header-row { @apply flex justify-between items-center gap-4 mb-1; }
.log-action { @apply font-bold text-sm text-profissa-text-main; }
.log-time { @apply text-xs text-profissa-text-dim font-mono flex-shrink-0; }
.log-meta { @apply flex gap-4 text-xs text-profissa-text-muted flex-wrap; }
.log-admin { @apply flex items-center gap-1; }
.log-entity { @apply flex items-center gap-1; }
.log-details { @apply text-xs text-profissa-text-dim mt-1 font-mono truncate; }
</style>
